import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, updateDoc, doc, writeBatch } from "firebase/firestore";
import { db, callActivateApplicant } from "../../lib/firebase";
import { Link } from "react-router-dom";
import "./ApplicationSubmissions.css";
import AdminPageHeader from "../../components/AdminPageHeader";
import useUserRole from "../../hooks/useUserRole";
import useRolePermissions from "../../hooks/useRolePermissions";

function formatAddress(app) {
  if (typeof app?.address === "string" && app.address.trim()) return app.address.trim();

  const a1 = (app?.address1 || app?.street || "").trim();
  const a2 = (app?.address2 || "").trim();
  const city = (app?.city || "").trim();
  const state = (app?.state || "").trim();
  const zip = (app?.zip || app?.postalCode || "").trim();

  const line1 = [a1, a2].filter(Boolean).join(" ");
  const line2 = [city, state].filter(Boolean).join(", ") + (zip ? ` ${zip}` : "");

  const out = [line1, line2.trim()].filter(Boolean).join("\n");
  return out || "—";
}

function normalizeAvailability(av) {
  if (!av) return [];
  if (Array.isArray(av)) return av.filter(Boolean).map((x) => String(x));
  if (typeof av === "object") {
    return Object.entries(av)
      .filter(([, v]) => v === true)
      .map(([k]) => String(k));
  }
  if (typeof av === "string") return [av];
  return [];
}

function normalizeArrayish(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") return [value];
  if (typeof value === "object") {
    return Object.entries(value)
      .filter(([, v]) => v === true)
      .map(([k]) => k);
  }
  return [];
}

function formatTimestamp(ts) {
  try {
    if (!ts) return "—";
    if (typeof ts?.toDate === "function") {
      return ts.toDate().toLocaleString();
    }
    if (ts?.seconds) {
      return new Date(ts.seconds * 1000).toLocaleString();
    }
    return String(ts);
  } catch {
    return "—";
  }
}

function getTimestampValue(ts) {
  try {
    if (!ts) return 0;
    if (typeof ts?.toDate === "function") return ts.toDate().getTime();
    if (ts?.seconds) return ts.seconds * 1000;
    const parsed = new Date(ts).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  } catch {
    return 0;
  }
}

function prettifyLabel(key) {
  const custom = {
    pastWorkYear: "Last Time Worked",
    workedBefore: "Worked Before",
    sponsorHospital: "Sponsor Hospital",
    businessPhone: "Business Phone",
    businessAddress: "Business Address",
    shirtSize: "Shirt Size",
    dea: "DEA #",
    npi: "NPI #",
    pinLast4: "PIN Last 4",
    createdAt: "Created At",
    updatedAt: "Updated At",
    updatedBy: "Updated By",
    emailKey: "Email Key",
    authUid: "Auth UID",
    uid: "UID",
  };

  if (custom[key]) return custom[key];

  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFieldValue(value, key = "") {
  if (value === null || value === undefined || value === "") return "—";

  if (
    key === "timestamp" ||
    key === "createdAt" ||
    key === "updatedAt" ||
    key === "activatedAt" ||
    key === "Submitted"
  ) {
    return formatTimestamp(value);
  }

  if (Array.isArray(value)) {
    return value.length ? value.filter(Boolean).join(", ") : "—";
  }

  if (typeof value === "object") {
    if (typeof value?.toDate === "function" || value?.seconds) {
      return formatTimestamp(value);
    }

    const truthyKeys = Object.entries(value)
      .filter(([, v]) => v === true)
      .map(([k]) => prettifyLabel(k));

    if (truthyKeys.length) return truthyKeys.join(", ");

    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

function isNewStaff(person) {
  const workedBefore = String(person?.workedBefore || "").trim().toLowerCase();
  return workedBefore === "" || workedBefore === "no";
}

function getApplicationRowClass(app) {
  const isNew = isNewStaff(app);
  const isActive = !!app?.active;

  if (isNew && !isActive) return "new-inactive-row";
  if (isNew && isActive) return "new-active-row";
  if (!isNew && !isActive) return "inactive-row";
  return "";
}

function normalizeApplication(d, id) {
  const nested = d?.applicationData && typeof d.applicationData === "object" ? d.applicationData : {};
  const merged = { ...nested, ...d };

  return {
    id,
    ...merged,

    name: merged?.name || "—",
    email: merged?.email || merged?.emailKey || id,
    position: merged?.position || "—",
    uid: merged?.uid || merged?.authUid || "—",

    active: typeof merged?.active === "boolean" ? merged.active : true,
    deleted: merged?.deleted === true,

    timestamp: merged?.timestamp || merged?.createdAt || merged?.activatedAt || null,

    phone: merged?.phone || merged?.cellPhone || "—",
    address: merged?.address || "",
    availability: merged?.availability || [],
    assignmentPreferences: merged?.assignmentPreferences || merged?.assignment || [],
    sponsorHospital: merged?.sponsorHospital || "",
    dea: merged?.dea || "",
    npi: merged?.npi || "",
    businessAddress: merged?.businessAddress || "",
    businessPhone: merged?.businessPhone || "",
    comments: merged?.comments || "",
    role: merged?.role || "Field User",

    workedBefore: merged?.workedBefore || "",
    pastWorkYear: merged?.pastWorkYear || "",
    shirtSize: merged?.shirtSize || "",

    _allData: merged,
  };
}

export default function ApplicationSubmissions() {
  const [applications, setApplications] = useState([]);
  const [selectedApps, setSelectedApps] = useState([]);
  const [viewingApp, setViewingApp] = useState(null);
  const [sortBy, setSortBy] = useState("dateDesc");
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [statusFilter, setStatusFilter] = useState(["active", "inactive"]);
  const [experienceFilter, setExperienceFilter] = useState(["new", "past"]);
  const [positionFilter, setPositionFilter] = useState([]);

  const { role, loading: roleLoading } = useUserRole();
  const { can, loading: permsLoading } = useRolePermissions(role);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    const snapshot = await getDocs(collection(db, "2026_Applications"));
    const data = snapshot.docs
      .map((d) => normalizeApplication(d.data(), d.id))
      .filter((app) => !app.deleted);
    setApplications(data);
  };

  const toggleSelect = (id) => {
    setSelectedApps((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const uniquePositions = useMemo(() => {
    return Array.from(
      new Set(
        applications
          .map((app) => String(app.position || "").trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [applications]);

const selectAllPositions = () => {
  setPositionFilter(uniquePositions);
};

const clearPositions = () => {
  setPositionFilter([]);
};

  const togglePositionFilter = (position) => {
    setPositionFilter((prev) =>
      prev.includes(position) ? prev.filter((p) => p !== position) : [...prev, position]
    );
  };

  const toggleSimpleFilter = (value, setState) => {
    setState((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setSortBy("dateDesc");
    setStatusFilter(["active", "inactive"]);
    setExperienceFilter(["new", "past"]);
    setPositionFilter([]);
  };

  const setApplicantInactive = async (app) => {
    const appRef = doc(db, "2026_Applications", app.id);
    await updateDoc(appRef, { active: false, status: "inactive" });

    if (app.uid && app.uid !== "—") {
      await updateDoc(doc(db, "users", app.uid), {
        active: false,
      });
    }
  };

  const setApplicantActive = async (app) => {
    const appRef = doc(db, "2026_Applications", app.id);

    if (app.uid && app.uid !== "—") {
      await updateDoc(appRef, { active: true, status: "active" });

      await updateDoc(doc(db, "users", app.uid), {
        active: true,
        availability: app.availability || [],
        assignment: app.assignmentPreferences || [],
        assignmentPreferences: app.assignmentPreferences || [],
        position: app.position || "",
        name: app.name || "",
        email: app.email || "",
        phone: app.phone || "",
        address: app.address || "",
        workedBefore: app.workedBefore || "",
        pastWorkYear: app.pastWorkYear || "",
      });
      return;
    }

    const res = await callActivateApplicant({
      emailKey: app.id,
      role: app.role || "Field User",
    });

    const returnedUid = res?.uid || res?.user?.uid || res?.data?.uid;

    const patch = {
      active: true,
      status: "active",
      ...(returnedUid ? { uid: returnedUid } : {}),
    };

    await updateDoc(appRef, patch);

    if (returnedUid) {
      await updateDoc(doc(db, "users", returnedUid), {
        active: true,
        availability: app.availability || [],
        assignment: app.assignmentPreferences || [],
        assignmentPreferences: app.assignmentPreferences || [],
        position: app.position || "",
        name: app.name || "",
        email: app.email || "",
        phone: app.phone || "",
        address: app.address || "",
        workedBefore: app.workedBefore || "",
        pastWorkYear: app.pastWorkYear || "",
      });
    }
  };

  const toggleActiveStatus = async (app) => {
    setErrorMsg("");
    try {
      if (app.active) {
        await setApplicantInactive(app);
      } else {
        await setApplicantActive(app);
      }
      await fetchApplications();
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.message || "Failed to update applicant status.");
    }
  };

  const batchSetActiveState = async (makeActive) => {
    setErrorMsg("");
    const appsToUpdate = applications.filter((app) => selectedApps.includes(app.id));
    if (!appsToUpdate.length) return;

    try {
      for (const app of appsToUpdate) {
        if (makeActive && !app.active) {
          await setApplicantActive(app);
        }
        if (!makeActive && app.active) {
          await setApplicantInactive(app);
        }
      }

      await fetchApplications();
    } catch (e) {
      console.error(e);
      setErrorMsg(
        e?.message || `Failed to ${makeActive ? "activate" : "deactivate"} selected applicants.`
      );
    }
  };

  const deleteSelected = async () => {
    setErrorMsg("");
    try {
      const promises = selectedApps.map((id) =>
        updateDoc(doc(db, "2026_Applications", id), { deleted: true })
      );
      await Promise.all(promises);
      setSelectedApps([]);
      await fetchApplications();
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.message || "Failed to delete selected.");
    }
  };

  const syncActiveApplicantsToUsers = async () => {
    setErrorMsg("");
    try {
      const batch = writeBatch(db);

      applications.forEach((app) => {
        if (!app.active) return;
        if (!app.uid || app.uid === "—") return;

        const userRef = doc(db, "users", app.uid);
        batch.set(
          userRef,
          {
            active: true,
            availability: app.availability || [],
            assignment: app.assignmentPreferences || [],
            assignmentPreferences: app.assignmentPreferences || [],
            position: app.position || "",
            name: app.name || "",
            email: app.email || "",
            phone: app.phone || "",
            address: app.address || "",
            workedBefore: app.workedBefore || "",
            pastWorkYear: app.pastWorkYear || "",
          },
          { merge: true }
        );
      });

      await batch.commit();
      await fetchApplications();
      alert("Active applicants synced to users.");
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.message || "Failed to sync applicants to users.");
    }
  };

  const filteredAndSortedApplications = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    const filtered = applications.filter((app) => {
      const name = String(app.name || "").toLowerCase();
      const email = String(app.email || "").toLowerCase();
      const position = String(app.position || "").toLowerCase();
      const activeKey = app.active ? "active" : "inactive";
      const experienceKey = isNewStaff(app) ? "new" : "past";

      const matchesSearch =
        !term || name.includes(term) || email.includes(term) || position.includes(term);

      const matchesStatus = statusFilter.includes(activeKey);
      const matchesExperience = experienceFilter.includes(experienceKey);
      const matchesPosition =
        positionFilter.length === 0 || positionFilter.includes(app.position);

      return matchesSearch && matchesStatus && matchesExperience && matchesPosition;
    });

    return filtered.sort((a, b) => {
      if (sortBy === "position") {
        const posCompare = String(a.position || "").localeCompare(String(b.position || ""));
        if (posCompare !== 0) return posCompare;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }

      if (sortBy === "name") {
        return String(a.name || "").localeCompare(String(b.name || ""));
      }

      if (sortBy === "dateAsc") {
        const aTime = getTimestampValue(a.timestamp);
        const bTime = getTimestampValue(b.timestamp);
        if (aTime !== bTime) return aTime - bTime;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }

      const aTime = getTimestampValue(a.timestamp);
      const bTime = getTimestampValue(b.timestamp);
      if (aTime !== bTime) return bTime - aTime;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [applications, searchTerm, sortBy, statusFilter, experienceFilter, positionFilter]);

  const allChecked =
    filteredAndSortedApplications.length > 0 &&
    filteredAndSortedApplications.every((app) => selectedApps.includes(app.id));

  const reviewRows = useMemo(() => {
    if (!viewingApp) return [];

    const baseRows = [
      ["Name", viewingApp.name],
      ["Email", viewingApp.email],
      ["Phone", viewingApp.phone],
      ["Address", formatAddress(viewingApp)],
      ["Position", viewingApp.position],
      ["Status", viewingApp.active ? "Active" : "Inactive"],
      ["UID", viewingApp.uid || "—"],
      ["Worked Before", viewingApp.workedBefore || "—"],
      ["Last Time Worked", viewingApp.pastWorkYear || "—"],
      ["Shirt Size", viewingApp.shirtSize || "—"],
      ["Availability", normalizeAvailability(viewingApp.availability)],
      ["Assignment Preferences", normalizeArrayish(viewingApp.assignmentPreferences)],
      ["Sponsor Hospital", viewingApp.sponsorHospital || "—"],
      ["DEA #", viewingApp.dea || "—"],
      ["NPI #", viewingApp.npi || "—"],
      ["Business Address", viewingApp.businessAddress || "—"],
      ["Business Phone", viewingApp.businessPhone || "—"],
      ["Comments", viewingApp.comments || "—"],
      ["Submitted", viewingApp.timestamp || viewingApp.createdAt || "—"],
      ["Updated At", viewingApp.updatedAt || "—"],
      ["Updated By", viewingApp.updatedBy || "—"],
    ];

    const alreadyShown = new Set([
      "id",
      "name",
      "email",
      "phone",
      "address",
      "address1",
      "address2",
      "street",
      "city",
      "state",
      "zip",
      "postalCode",
      "position",
      "active",
      "status",
      "uid",
      "workedBefore",
      "pastWorkYear",
      "shirtSize",
      "availability",
      "assignmentPreferences",
      "assignment",
      "sponsorHospital",
      "dea",
      "npi",
      "businessAddress",
      "businessPhone",
      "comments",
      "timestamp",
      "createdAt",
      "updatedAt",
      "updatedBy",
      "deleted",
      "role",
      "_allData",
      "applicationData",
      "authUid",
      "emailKey",
      "pinLast4",
      "ssn",
    ]);

    const extraRows = Object.entries(viewingApp._allData || {})
      .filter(([key, value]) => {
        if (alreadyShown.has(key)) return false;
        if (value === undefined || value === null || value === "") return false;
        return true;
      })
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => [prettifyLabel(key), value]);

    return [...baseRows, ...extraRows].filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    });
  }, [viewingApp]);

  if (roleLoading || permsLoading) {
    return <div style={{ padding: 20 }}>Loading permissions…</div>;
  }

  if (!can("ApplicationsRead")) {
    return (
      <div style={{ padding: 20 }}>
        <AdminPageHeader title="Applications" />
        <p>You do not have access to this page.</p>
      </div>
    );
  }

  return (
    <div className="applications-dashboard">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>2026 Application Submissions</h2>

        <Link
          to="/admin"
          style={{
            border: "1px solid rgba(15,23,42,0.15)",
            padding: "8px 12px",
            borderRadius: 10,
            textDecoration: "none",
            fontWeight: 700,
            background: "#fff",
          }}
        >
          ← Return to Admin Portal
        </Link>
      </div>

      {!!errorMsg && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            color: "#7f1d1d",
            padding: "10px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {errorMsg}
        </div>
      )}

      <div className="controls" style={{ display: "grid", gap: 12 }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
          }}
        >
          <label htmlFor="application-sort">Sort by:</label>
          <select
            id="application-sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="dateDesc">Date Submitted (Newest)</option>
            <option value="dateAsc">Date Submitted (Oldest)</option>
            <option value="position">Position</option>
            <option value="name">Name</option>
          </select>

          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, email, or position..."
            aria-label="Search applications"
            style={{
              minWidth: 260,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid rgba(15,23,42,0.15)",
              background: "#fff",
            }}
          />

          <button onClick={clearAllFilters}>Clear Filters</button>
        </div>

        <div className="applications-filter-panel">
          <div className="applications-filter-group">
            <div className="applications-filter-title">Status</div>
            <div className="applications-filter-chips">
              <button
                type="button"
                onClick={() => toggleSimpleFilter("active", setStatusFilter)}
                className={statusFilter.includes("active") ? "filter-chip active" : "filter-chip"}
              >
                Active
              </button>
              <button
                type="button"
                onClick={() => toggleSimpleFilter("inactive", setStatusFilter)}
                className={statusFilter.includes("inactive") ? "filter-chip active" : "filter-chip"}
              >
                Inactive
              </button>
            </div>
          </div>

          <div className="applications-filter-group" style={{ marginTop: 12 }}>
            <div className="applications-filter-title">Applicant Type</div>
            <div className="applications-filter-chips">
              <button
                type="button"
                onClick={() => toggleSimpleFilter("new", setExperienceFilter)}
                className={experienceFilter.includes("new") ? "filter-chip active" : "filter-chip"}
              >
                New Applicant
              </button>
              <button
                type="button"
                onClick={() => toggleSimpleFilter("past", setExperienceFilter)}
                className={experienceFilter.includes("past") ? "filter-chip active" : "filter-chip"}
              >
                Past Employee
              </button>
            </div>
          </div>

          <div className="applications-filter-group" style={{ marginTop: 12 }}>
            <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6
  }}
>
  <div className="applications-filter-title">Position</div>

  <div style={{ display: "flex", gap: 6 }}>
    <button
      type="button"
      onClick={selectAllPositions}
      style={{ fontSize: 12, padding: "4px 8px" }}
    >
      Select All
    </button>

    <button
      type="button"
      onClick={clearPositions}
      style={{ fontSize: 12, padding: "4px 8px" }}
    >
      Clear
    </button>
  </div>
</div>

<div className="applications-filter-chips">
  {uniquePositions.map((position) => (
    <button
      key={position}
      type="button"
      onClick={() => togglePositionFilter(position)}
      className={
        positionFilter.includes(position)
          ? "filter-chip active"
          : "filter-chip"
      }
    >
      {position}
    </button>
  ))}

  {uniquePositions.length === 0 && <span>No positions found.</span>}
</div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
          }}
        >
          <button onClick={() => batchSetActiveState(true)} disabled={selectedApps.length === 0}>
            Make Active
          </button>

          <button onClick={() => batchSetActiveState(false)} disabled={selectedApps.length === 0}>
            Make Inactive
          </button>

          <button onClick={deleteSelected} disabled={selectedApps.length === 0}>
            Delete Selected
          </button>

          <button onClick={syncActiveApplicantsToUsers}>
            Sync Active Applicants to Users
          </button>
        </div>
      </div>

      <div className="applications-summary-row">
        Showing {filteredAndSortedApplications.length} applications
      </div>

      <div className="applications-table-wrap">
        <table className="applications-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) =>
                    setSelectedApps(
                      e.target.checked
                        ? Array.from(
                            new Set([
                              ...selectedApps,
                              ...filteredAndSortedApplications.map((app) => app.id),
                            ])
                          )
                        : selectedApps.filter(
                            (id) => !filteredAndSortedApplications.some((app) => app.id === id)
                          )
                    )
                  }
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Position</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>UID</th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedApplications.map((app) => (
              <tr key={app.id} className={getApplicationRowClass(app)}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedApps.includes(app.id)}
                    onChange={() => toggleSelect(app.id)}
                  />
                </td>
                <td>{app.name}</td>
                <td>{app.email}</td>
                <td>{app.position}</td>
                <td>{formatTimestamp(app.timestamp)}</td>
                <td>
                  <button onClick={() => toggleActiveStatus(app)}>
                    {app.active ? "Active" : "Inactive"}
                  </button>
                </td>
                <td style={{ fontSize: 12 }}>{app.uid || "—"}</td>
                <td>
                  <button onClick={() => setViewingApp(app)}>Review</button>
                </td>
              </tr>
            ))}

            {filteredAndSortedApplications.length === 0 && (
              <tr>
                <td colSpan="8" style={{ textAlign: "center", padding: "16px" }}>
                  No applications found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewingApp && (
        <div className="application-modal-overlay">
          <div className="application-modal">
            <h2>Review Application</h2>

            <div className="application-details-grid">
              {reviewRows.map(([label, value]) => {
                const isLongField =
                  label === "Address" ||
                  label === "Business Address" ||
                  label === "Comments" ||
                  label === "Availability" ||
                  label === "Assignment Preferences" ||
                  (typeof value === "object" && !Array.isArray(value));

                return (
                  <div
                    key={label}
                    style={{
                      gridColumn: isLongField ? "1 / -1" : "auto",
                      whiteSpace:
                        typeof value === "object" && !Array.isArray(value)
                          ? "pre-wrap"
                          : label === "Address" || label === "Business Address"
                            ? "pre-line"
                            : "normal",
                    }}
                  >
                    <strong>{label}:</strong>{" "}
                    {Array.isArray(value) ? (
                      value.length ? (
                        <ul style={{ marginTop: 6, marginBottom: 0 }}>
                          {value.map((item, i) => (
                            <li key={`${label}-${i}`}>{String(item)}</li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )
                    ) : (
                      formatFieldValue(value, label)
                    )}
                  </div>
                );
              })}
            </div>

            <button className="close-modal-button" onClick={() => setViewingApp(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}